import { Building2, Plus, RotateCcw, Save, Trash2, Upload } from "lucide-react";
import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";

import { useAppSettings } from "@/components/app-settings/app-settings-provider";
import type { CompanyRow } from "@/components/settings/settings-types";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/lib/client/api";

type CompaniesResponse = {
  companies: CompanyRow[];
};

type CompanyForm = {
  type: "own" | "client" | "agency" | "vendor";
  nameEn: string;
  nameZh: string;
  logoFileId: string;
  address: string;
  taxId: string;
  website: string;
  contactInfo: string;
};

type UploadResponse = {
  id: string;
  licenseWarning: string | null;
};

const emptyCompanyForm: CompanyForm = {
  type: "client",
  nameEn: "",
  nameZh: "",
  logoFileId: "",
  address: "",
  taxId: "",
  website: "",
  contactInfo: "",
};

export function CompanySettings() {
  const { language } = useAppSettings();
  const text = language === "zh" ? zh : en;
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [form, setForm] = useState<CompanyForm>(emptyCompanyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [trash, setTrash] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function loadCompanies() {
    setLoading(true);
    setError(null);

    try {
      const response = await apiGet<CompaniesResponse>(trash ? "/api/settings/companies?trash=1" : "/api/settings/companies");
      setCompanies(response.companies);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : text.loadError);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, trash]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);

    const payload = {
      type: form.type,
      nameEn: form.nameEn.trim(),
      nameZh: emptyToNull(form.nameZh),
      logoFileId: emptyToNull(form.logoFileId),
      address: emptyToNull(form.address),
      taxId: emptyToNull(form.taxId),
      website: emptyToNull(form.website),
      contactInfo: emptyToNull(form.contactInfo),
    };

    try {
      if (editingId) {
        await apiPatch<typeof payload, { ok: true }>(`/api/settings/companies/${editingId}`, payload);
        setMessage(text.saved);
      } else {
        await apiPost<typeof payload, { id: string }>("/api/settings/companies", payload);
        setMessage(text.created);
      }

      setEditingId(null);
      setForm(emptyCompanyForm);
      await loadCompanies();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : text.saveError);
    } finally {
      setSaving(false);
    }
  }

  async function handleLogoUpload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setUploading(true);
    setError(null);
    setMessage(null);

    try {
      const formData = new FormData();
      formData.set("file", file);
      formData.set("purpose", form.type === "own" ? "company_logo" : "client_logo");

      const response = await fetch("/api/files/upload", {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      const payload = (await response.json()) as UploadResponse | { error?: string };

      if (!response.ok || !("id" in payload)) {
        throw new Error("error" in payload && payload.error ? payload.error : text.uploadError);
      }

      setForm((current) => ({ ...current, logoFileId: payload.id }));
      setMessage(text.uploaded);
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : text.uploadError);
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  }

  async function mutateCompany(company: CompanyRow, action: "trash" | "restore" | "permanent") {
    const confirmation = action === "trash" ? text.confirmTrash : action === "permanent" ? text.confirmPermanent : null;
    if (confirmation && !window.confirm(confirmation)) return;

    setActionId(company.id);
    setError(null);
    setMessage(null);

    try {
      if (action === "trash") await apiDelete<{ ok: true }>(`/api/settings/companies/${company.id}`);
      if (action === "restore") await apiPost<Record<string, never>, { ok: true }>(`/api/settings/companies/${company.id}/restore`, {});
      if (action === "permanent") await apiDelete<{ ok: true }>(`/api/settings/companies/${company.id}/permanent`);
      setEditingId(null);
      setForm(emptyCompanyForm);
      setMessage(action === "trash" ? text.deleted : action === "restore" ? text.restored : text.permanentlyDeleted);
      await loadCompanies();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : text.actionError);
    } finally {
      setActionId(null);
    }
  }

  return (
    <section className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">{text.title}</h1>
        <p className="text-sm text-muted-foreground">{text.description}</p>
      </header>

      <StatusMessage message={message} error={error} />

      <div className="flex gap-2">
        <Button type="button" variant={trash ? "secondary" : "primary"} onClick={() => setTrash(false)}>{text.companies}</Button>
        <Button type="button" variant={trash ? "primary" : "secondary"} onClick={() => setTrash(true)}>{text.trash}</Button>
      </div>

      {!trash ? <Card className="p-4">
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid gap-3 md:grid-cols-4">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted-foreground">{text.type}</span>
              <select className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm" value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value as CompanyForm["type"] })}>
                <option value="own">{text.own}</option>
                <option value="client">{text.client}</option>
                <option value="agency">{text.agency}</option>
                <option value="vendor">{text.vendor}</option>
              </select>
            </label>
            <Field label={text.nameEn} value={form.nameEn} onChange={(value) => setForm({ ...form, nameEn: value })} />
            <Field label={text.nameZh} value={form.nameZh} onChange={(value) => setForm({ ...form, nameZh: value })} />
            <Field label={text.taxId} value={form.taxId} onChange={(value) => setForm({ ...form, taxId: value })} />
          </div>
          <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
            <Field label={text.website} value={form.website} onChange={(value) => setForm({ ...form, website: value })} />
            <Field label={text.logoFileId} value={form.logoFileId} onChange={(value) => setForm({ ...form, logoFileId: value })} />
            <label className="mt-5 inline-flex h-9 cursor-pointer items-center justify-center gap-2 rounded-md border border-border px-3 text-sm font-medium transition hover:bg-muted">
              <Upload className="h-4 w-4" aria-hidden="true" />
              {uploading ? text.uploading : text.upload}
              <input type="file" accept="image/*" className="hidden" onChange={(event) => void handleLogoUpload(event)} disabled={uploading} />
            </label>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted-foreground">{text.address}</span>
              <Textarea value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted-foreground">{text.contactInfo}</span>
              <Textarea value={form.contactInfo} onChange={(event) => setForm({ ...form, contactInfo: event.target.value })} />
            </label>
          </div>
          <div className="flex justify-between">
            <Button type="button" variant="secondary" onClick={() => resetForm()}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              {text.newCompany}
            </Button>
            <Button type="submit" disabled={saving}>
              <Save className="h-4 w-4" aria-hidden="true" />
              {saving ? text.saving : editingId ? text.save : text.create}
            </Button>
          </div>
        </form>
      </Card> : null}

      <div className="grid gap-3 lg:grid-cols-2">
        {loading ? <Card className="p-4 text-sm text-muted-foreground">{text.loading}</Card> : null}
        {!loading && companies.length === 0 ? <Card className="p-4 text-sm text-muted-foreground">{text.empty}</Card> : null}
        {!loading
          ? companies.map((company) => (
              <Card key={company.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-primary" aria-hidden="true" />
                      <h2 className="truncate font-medium">{language === "zh" && company.nameZh ? company.nameZh : company.nameEn}</h2>
                    </div>
                    <p className="mt-1 text-xs uppercase text-muted-foreground">{company.type}</p>
                  </div>
                  <div className="flex gap-2">
                    {!trash ? <Button type="button" variant="secondary" className="h-8 px-2" onClick={() => editCompany(company)}>{text.edit}</Button> : null}
                    {!trash ? <Button type="button" variant="secondary" className="h-8 w-8 p-0 text-destructive" title={text.delete} disabled={actionId === company.id} onClick={() => void mutateCompany(company, "trash")}><Trash2 className="h-4 w-4" aria-hidden="true" /></Button> : null}
                    {trash ? <Button type="button" variant="secondary" className="h-8 w-8 p-0" title={text.restore} disabled={actionId === company.id} onClick={() => void mutateCompany(company, "restore")}><RotateCcw className="h-4 w-4" aria-hidden="true" /></Button> : null}
                    {trash ? <Button type="button" variant="secondary" className="h-8 w-8 p-0 text-destructive" title={text.permanent} disabled={actionId === company.id} onClick={() => void mutateCompany(company, "permanent")}><Trash2 className="h-4 w-4" aria-hidden="true" /></Button> : null}
                  </div>
                </div>
                <div className="mt-3 grid gap-1 text-sm text-muted-foreground">
                  {company.website ? <span>{company.website}</span> : null}
                  {company.taxId ? <span>{company.taxId}</span> : null}
                  {company.logoFileId ? <span>{text.logoBound}</span> : null}
                  {trash && company.deletedAt ? <span>{text.deletedAt}: {new Date(company.deletedAt).toLocaleString()}</span> : null}
                </div>
              </Card>
            ))
          : null}
      </div>
    </section>
  );

  function editCompany(company: CompanyRow) {
    setEditingId(company.id);
    setForm({
      type: company.type,
      nameEn: company.nameEn,
      nameZh: company.nameZh ?? "",
      logoFileId: company.logoFileId ?? "",
      address: company.address ?? "",
      taxId: company.taxId ?? "",
      website: company.website ?? "",
      contactInfo: company.contactInfo ?? "",
    });
    setMessage(null);
    setError(null);
  }

  function resetForm() {
    setEditingId(null);
    setForm(emptyCompanyForm);
    setMessage(null);
    setError(null);
  }
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      <Input value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function StatusMessage({ message, error }: { message: string | null; error: string | null }) {
  if (!message && !error) return null;

  return (
    <div className={error ? "rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" : "rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary"}>
      {error ?? message}
    </div>
  );
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

const en = {
  title: "Company",
  description: "Maintain VZEN, client, agency, and vendor profiles for quotations.",
  companies: "Companies",
  trash: "Recycle bin",
  type: "Type",
  own: "Own company",
  client: "Client",
  agency: "Agency",
  vendor: "Vendor",
  nameEn: "English name",
  nameZh: "Chinese name",
  logoFileId: "Logo file ID",
  taxId: "Tax ID",
  website: "Website",
  address: "Address",
  contactInfo: "Contact info",
  upload: "Upload logo",
  uploading: "Uploading...",
  newCompany: "New company",
  create: "Create company",
  save: "Save company",
  saving: "Saving...",
  edit: "Edit",
  loading: "Loading companies...",
  empty: "No companies yet.",
  logoBound: "Logo attached",
  uploaded: "Logo uploaded and attached to the form.",
  created: "Company created.",
  saved: "Company saved.",
  deleted: "Company moved to the recycle bin.",
  restored: "Company restored.",
  permanentlyDeleted: "Company permanently deleted.",
  delete: "Move to recycle bin",
  restore: "Restore",
  permanent: "Delete permanently",
  deletedAt: "Deleted",
  confirmTrash: "Move this company to the recycle bin? Existing quotations will keep their reference.",
  confirmPermanent: "Permanently delete this company? This cannot be undone.",
  actionError: "Unable to update company.",
  loadError: "Unable to load companies.",
  saveError: "Unable to save company.",
  uploadError: "Unable to upload logo.",
};

const zh: typeof en = {
  title: "公司",
  description: "维护 VZEN、客户、代理和供应商资料，用于报价单。",
  companies: "公司",
  trash: "回收站",
  type: "类型",
  own: "己方公司",
  client: "客户",
  agency: "代理",
  vendor: "供应商",
  nameEn: "英文名称",
  nameZh: "中文名称",
  logoFileId: "Logo 文件 ID",
  taxId: "税号",
  website: "网站",
  address: "地址",
  contactInfo: "联系人信息",
  upload: "上传 Logo",
  uploading: "上传中...",
  newCompany: "新公司",
  create: "创建公司",
  save: "保存公司",
  saving: "保存中...",
  edit: "编辑",
  loading: "正在加载公司...",
  empty: "暂无公司。",
  logoBound: "已绑定 Logo",
  uploaded: "Logo 已上传并填入表单。",
  created: "公司已创建。",
  saved: "公司已保存。",
  deleted: "公司已移入回收站。",
  restored: "公司已恢复。",
  permanentlyDeleted: "公司已最终删除。",
  delete: "移入回收站",
  restore: "恢复",
  permanent: "最终删除",
  deletedAt: "删除时间",
  confirmTrash: "将此公司移入回收站？已有报价仍会保留引用。",
  confirmPermanent: "最终删除此公司？此操作无法撤销。",
  actionError: "无法更新公司。",
  loadError: "无法加载公司。",
  saveError: "无法保存公司。",
  uploadError: "无法上传 Logo。",
};
